/**
 * MaintenanceSalaryCombinationCodePicker - Wrapper around generic EntityPicker
 * 
 * Provides specific configuration for Maintenance Salary Combination Codes
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';
import { EntityPickerSelect } from '../EntityPicker/EntityPickerSelect';

// ── MaintenanceSalaryCombinationCode-specific configuration ──────────────────
export const MAINTENANCE_SALARY_COMBINATION_CODE_CONFIG = {
  apiEndpoint: "/api/MaintenanceSalaryCombinationCode/active",
  entityName: "Maintenance Salary Combination Code",
  idField: "id",
  displayFields: {
    primary: "code",
    secondary: "amount"
  },
  tableHeaders: ["Code", "Amount"],
  tableAccessors: ["code", "amount"],
  searchFields: [
    { label: "All", field: "all", index: 0 },
    { label: "Code", field: "code", index: 1 },
    { label: "Amount", field: "amount", index: 2 },
  ],
  placeholder: "--Select salary combination code--"
};

// ── MaintenanceSalaryCombinationCodePickerSelect wrapper ──────────────────────
/**
 * MaintenanceSalaryCombinationCodePickerSelect
 * 
 * Usage:
 * <MaintenanceSalaryCombinationCodePickerSelect
 *   label="Salary Combination Code"
 *   value={form.maintenanceSalaryCombinationCodeId}
 *   onSelect={(val) => setCode(val)}
 * />
 */
export function MaintenanceSalaryCombinationCodePickerSelect({
  label,
  value,   // The ID value
  onSelect,
  onClear,
  placeholder = "--Select salary combination code--",
  allowAll,
  disabled = false,
  isRequired = false,
  className = "",
  error = false,
  errorMessage = ""
}) {
  return (
    <div className="mb-1 w-full">
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className={error ? "rounded-md ring-1 ring-red-500" : ""}>
        <EntityPickerSelect
          config={MAINTENANCE_SALARY_COMBINATION_CODE_CONFIG}
          value={value ? { id: value } : null}
          disabled={disabled}
          placeholder={placeholder}
          allowAll={allowAll}
          className={className}
          onChange={(val) => {
            if (val === null) {
              if (onClear) onClear();
            } else {
              // Returns the full object {id, code, amount}
              if (onSelect) onSelect(val);
            }
          }}
        />
      </div>

      {error && (
        <span className="mt-1 block text-sm text-red-500">
          {errorMessage}
        </span>
      )}
    </div>
  );
}

// ── MaintenanceSalaryCombinationCodePickerField wrapper ───────────────────────
/**
 * MaintenanceSalaryCombinationCodePickerField
 */
export function MaintenanceSalaryCombinationCodePickerField({
  maintenanceSalaryCombinationCodeId,
  displayValue = "",
  onSelect,
  onClear,
  ...rest
}) {
  return (
    <EntityPickerField
      {...rest}
      config={MAINTENANCE_SALARY_COMBINATION_CODE_CONFIG}
      entityId={maintenanceSalaryCombinationCodeId}
      displayValue={displayValue}
      onSelect={onSelect}
      onClear={onClear}
    />
  );
}

// ── MaintenanceSalaryCombinationCodePickerModal wrapper ───────────────────────
/**
 * MaintenanceSalaryCombinationCodePickerModal
 */
export function MaintenanceSalaryCombinationCodePickerModal({ isOpen, onClose, setData }) {
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={MAINTENANCE_SALARY_COMBINATION_CODE_CONFIG}
    />
  );
}

export { MaintenanceSalaryCombinationCodePickerField as default };